import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CursoService from '../../services/curso.service';
import { useTranslation } from '../../hooks/useTranslation';

const CursoList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    setLoading(true);
    try {
      const response = await CursoService.getAll();
      setCursos(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching cursos:', error);
      showSnackbar(t('cursos.errorLoadingCursos') + ': ' + (error.response?.data?.message || error.message), 'error');
      setCursos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await CursoService.delete(deleteDialog.id);
      showSnackbar(t('cursos.cursoDeletedSuccess'));
      fetchCursos();
    } catch (error) {
      showSnackbar(t('cursos.errorDeletingCurso'), 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredCursos = cursos.filter(curso =>
    curso.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('cursos.title')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('cursos.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/cursos/deleted')}
                >
                  {t('cursos.deletedCursos')}
                </Button>
              )}
              {hasPermission('cursos.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/cursos/new')}
                >
                  {t('cursos.newCurso')}
                </Button>
              )}
            </Box>
          </Box>

          <TextField
            placeholder={t('cursos.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('students.createdAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredCursos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('cursos.noCursosFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCursos.map((curso) => (
                    <TableRow key={curso.id} hover>
                      <TableCell>{curso.name}</TableCell>
                      <TableCell>{formatDate(curso.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('cursos.edit') && (
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/cursos/${curso.id}/edit`)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {hasPermission('cursos.delete') && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(curso.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('cursos.deleteCurso')}</DialogTitle>
        <DialogContent>
          <Typography>{t('cursos.deleteCursoConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CursoList;
